import { PaginatedDataDTO } from "@bottega52/commons-pagination";
import { Button, Card } from "@material-ui/core";
import { WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import WarningIcon from '@material-ui/icons/Warning';
import * as React from "react";
import { ConnectedProps, connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as CustomerContractServiceDecoder from '../../codec/customerContractServiceInDTODecoder';
import * as CreditsActions from '../../redux/credits/credits.actions';
import * as ModalsActions from '../../redux/modals/modals.actions';
import * as SettingsActions from '../../redux/settings/settings.actions';
import { IState } from '../../redux/store';
import { ICustomerContractInDTO } from "../../repository/jago/model/input/ICustomerContractInDTO";
import { ICustomerContractServiceFormDTO } from "../../repository/jago/model/input/ICustomerContractServiceFormDTO";
import { ICustomerContractServiceInDTO } from "../../repository/jago/model/input/ICustomerContractServiceInDTO";
import { ICustomerInDTO } from "../../repository/jago/model/input/ICustomerInDTO";
import AbilityProvider from "../../services/PermissionService/AbilityProvider";
import { PERMISSIONS } from "../../services/PermissionService/PermissionConstants";
import CustomerContractServiceForm from "../Forms/CustomerContractServiceForm";
import { ModalTypes } from "../Modals/ModalTypes";
import ReduxLanguage from "../ReduxLanguage";
import CustomerContractServicesTable from "./CustomerContractServicesTable";

const styles = createStyles({
  container: {
    display: 'flex',
    marginTop: 20,
    flexDirection: 'column',
  },
  cardContainer: {
    padding: 10,
    borderLeft: '3px solid #5AC0B1',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerInner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTextContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  icon: {
    marginRight: 5,
    fontSize: 25,
  },
  title: {
    fontWeight: 'bold'
  },
  tableContainer: {
    display: 'flex',
    backgroundColor: 'white',
    height: 'calc(100vh - 380px)',
    width: '100%',
    marginTop: 10
  }
});

export interface ICustomerContractServicesSectionState {
  pageSize: number;
}

type IReduxProps = ConnectedProps<typeof connector> & RouteComponentProps<any>;

export interface ICustomerContractServicesSectionProps extends WithStyles<typeof styles> {
  language: string;
  customer: ICustomerInDTO;
}

export type ComponentProps = ICustomerContractServicesSectionProps & IReduxProps;

class CustomerContractServicesSection extends React.Component<ComponentProps, ICustomerContractServicesSectionState> {

  constructor(props: ComponentProps) {
    super(props);
    this.state  = {
      pageSize: 1000
    };
  }

  async onOpenEditCustomerContractServiceForm(customerContractService: ICustomerContractServiceInDTO) {
    const { dispatch } = this.props;
    const customerContractServiceFormData = CustomerContractServiceDecoder.decodeCustomerContractServiceContentToForm(customerContractService);
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_CUSTOMER_CONTRACT_SERVICE_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: (
          <CustomerContractServiceForm
            customerContractService={customerContractServiceFormData}
            onCreateEditCustomerContractService={(newCustomerContractServiceData: ICustomerContractServiceFormDTO) => this.onEditCustomerContractServiceRequest(customerContractService.id, newCustomerContractServiceData)}
          />
        ),
        titleMessageKey: 'credits.customerContractServiceFormTitle',
      }
    }));
  }

  async onOpenNewCustomerContractServiceForm() {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_CUSTOMER_CONTRACT_SERVICE_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL_BACKDROP,
      modalProps: {
        content: (
          <CustomerContractServiceForm
            onCreateEditCustomerContractService={(newCustomerContractServiceData: ICustomerContractServiceFormDTO) => this.onCreateNewCustomerContractServiceRequest(newCustomerContractServiceData)}
          />
        ),
        titleMessageKey: 'credits.customerContractServiceFormTitle',
      }
    }));
  }

  async onOpenDuplicateCustomerContractServiceForm(customerContractService: ICustomerContractServiceInDTO) {
    const { dispatch } = this.props;
    const customerContractServiceFormData = CustomerContractServiceDecoder.decodeCustomerContractServiceContentToForm(customerContractService);
    // Remove the id to create a new service instead of editing
    const duplicatedServiceData = {
      ...customerContractServiceFormData,
      activationDate: null,
      deactivationDate: null,      
      id: null
    };
    
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_CUSTOMER_CONTRACT_SERVICE_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL_BACKDROP,
      modalProps: {
        content: (
          <CustomerContractServiceForm
            customerContractService={duplicatedServiceData}
            onCreateEditCustomerContractService={(newCustomerContractServiceData: ICustomerContractServiceFormDTO) => this.onCreateNewCustomerContractServiceRequest(newCustomerContractServiceData)}
          />
        ),
        titleMessageKey: 'credits.duplicateCustomerContractServiceFormTitle',
      }
    }));
  }

  public onEditCustomerContractServiceRequest(customerContractServiceId: number, newCustomerContractServiceData: ICustomerContractServiceFormDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`CREATE_CUSTOMER_CONTRACT_SERVICE_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'credits.editCustomerContractService',
        successMessageKey: 'credits.editCustomerContractServiceConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onEditCustomerContractServiceConfirm(customerContractServiceId, newCustomerContractServiceData),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_CONTRACT_SERVICE_CONFIRM_MODAL`)),
      }
    }));
  }

  public async onEditCustomerContractServiceConfirm(customerContractServiceId: number, newCustomerContractServiceData: ICustomerContractServiceFormDTO) {
    const { dispatch, customer, selectedCustomerContract } = this.props;
    try {
      dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_CONTRACT_SERVICE_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const newCustomerContractServiceDataEncoded = CustomerContractServiceDecoder.encodeCustomerContractServiceFromForm(newCustomerContractServiceData);
      const customerContractServiceUpdated = await dispatch<any>(CreditsActions.editCustomerContractService(customer.id, (selectedCustomerContract as ICustomerContractInDTO).id, customerContractServiceId, newCustomerContractServiceDataEncoded));
      dispatch<any>(ModalsActions.hideModal(`OPERATIONAL_CUSTOMER_CONTRACT_SERVICE_MODAL`));
      dispatch<any>(CreditsActions.setSelectedCustomerContractService(customerContractServiceUpdated));
      const promises = [
        dispatch<any>(CreditsActions.fetchCustomerContractServices({ page: 0, pageSize: 1000, customerId: customer.id, contractId: (selectedCustomerContract as ICustomerContractInDTO).id })),
        dispatch<any>(CreditsActions.fetchCustomerAllContractServices({pageSize: 200, customerId: customer.id })),
      ]
      if ((window?.location?.pathname || "").startsWith('/contractServices')) {
        promises.push(dispatch<any>(CreditsActions.fetchAllCustomerContractServices({ page: 0, pageSize: 50 })));
      }
      await Promise.all(promises) 
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      console.log(error)
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_CONTRACT_SERVICE_CREATION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'credits.createCustomerContractServiceError',
        }
      }));
    }
  }

  public onCreateNewCustomerContractServiceRequest(newCustomerContractServiceData: ICustomerContractServiceFormDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`CREATE_CUSTOMER_CONTRACT_SERVICE_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'credits.newCustomerContractService',
        successMessageKey: 'credits.customerContractServiceCreationConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onCreateNewCustomerContractServiceConfirm(newCustomerContractServiceData),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_CONTRACT_SERVICE_CONFIRM_MODAL`)),
      }
    }));
  }

  public async onCreateNewCustomerContractServiceConfirm(newCustomerContractServiceData: ICustomerContractServiceFormDTO) {
    const { dispatch, customer, selectedCustomerContract } = this.props;
    try {
      dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_CONTRACT_SERVICE_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const newCustomerContractServiceDataEncoded = CustomerContractServiceDecoder.encodeCustomerContractServiceFromForm(newCustomerContractServiceData);
      await dispatch<any>(CreditsActions.createNewCustomerContractService(customer.id, (selectedCustomerContract as ICustomerContractInDTO).id, newCustomerContractServiceDataEncoded));
      dispatch<any>(ModalsActions.hideModal(`OPERATIONAL_CUSTOMER_CONTRACT_SERVICE_MODAL`));     
      const promises = [
        dispatch<any>(CreditsActions.fetchCustomerContractServices({ page: 0, pageSize: 1000, customerId: customer.id, contractId: (selectedCustomerContract as ICustomerContractInDTO).id })),
        dispatch<any>(CreditsActions.fetchCustomerAllContractServices({pageSize: 200, customerId: customer.id })),
      ]
      if ((window?.location?.pathname || "").startsWith('/contractServices')) {
        promises.push(dispatch<any>(CreditsActions.fetchAllCustomerContractServices({ page: 0, pageSize: 50 })));
      }
      await Promise.all(promises) 
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_CONTRACT_SERVICE_CREATION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'credits.createCustomerContractServiceError',
        }
      }));
    }
  }

  async onFetchCustomerContractServicesOnPage (page) {
    const { dispatch, customer, selectedCustomerContract } = this.props;
    await dispatch<any>(CreditsActions.fetchCustomerContractServices({ page, pageSize: 1000, customerId: customer.id, contractId: (selectedCustomerContract as ICustomerContractInDTO).id }));
  }

  async onPageSizeChange(pageSize: number) {
    const { dispatch, customer, selectedCustomerContract } = this.props;
    this.setState({ pageSize }, async () => {
      await dispatch<any>(CreditsActions.fetchCustomerContractServices({ page: 0, pageSize, customerId: customer.id, contractId: (selectedCustomerContract as ICustomerContractInDTO).id }));
    });
  }

  onDeleteCustomerContractServiceRequest(customerContractService: ICustomerContractServiceInDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`DELETE_CUSTOMER_CONTRACT_SERVICE_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'forms.warning',
        successMessageKey: 'credits.deleteCustomerContractServiceConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onDeleteCustomerContractServiceConfirm(customerContractService),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`DELETE_CUSTOMER_CONTRACT_SERVICE_CONFIRM_MODAL`)),
      }
    }));
  }

  async onDeleteCustomerContractServiceConfirm(customerContractService: ICustomerContractServiceInDTO) {
    const { dispatch, customer, selectedCustomerContract } = this.props;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(CreditsActions.deleteCustomerContractService(customerContractService.customerId, customerContractService.customerContractId, customerContractService.id));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.hideModal('DELETE_CUSTOMER_CONTRACT_SERVICE_CONFIRM_MODAL'));
      const promises = [
        dispatch<any>(CreditsActions.fetchCustomerContractServices({ page: 0, pageSize: 1000, customerId: customer.id, contractId: (selectedCustomerContract as ICustomerContractInDTO).id })),
        dispatch<any>(CreditsActions.fetchCustomerAllContractServices({pageSize: 200, customerId: customer.id })),
      ]
      if ((window?.location?.pathname || "").startsWith('/contractServices')) {
        promises.push(dispatch<any>(CreditsActions.fetchAllCustomerContractServices({ page: 0, pageSize: 50 })));
      }
      await Promise.all(promises) 
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_CONTRACT_SERVICE_DELETE`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'credits.deleteCustomerContractServiceError',
        }
      }));
    }
  }
  
  public render() {
    const { customerContractServices, classes } = this.props;
    const { pageSize } = this.state;
    
    const canCreateCustomerContractService = AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACT_SERVICES_CREATE)

    return (
      <div className={classes.container}>
        <Card className={classes.cardContainer}>
          <div className={classes.header}>
            <div className={classes.headerInner}>
              <LocalOfferOutlinedIcon className={classes.icon} />
              <h3 className={classes.title}>{<ReduxLanguage languageKey="credits.contractServices"/>}</h3>
              <h5 className={classes.title} style={{ marginLeft: 0 }}>{` (${((customerContractServices as PaginatedDataDTO<ICustomerContractServiceInDTO>).content || []).length})`}</h5>
            </div>
            {canCreateCustomerContractService && 
              <Button
                variant="contained"
                style={{ fontWeight: 'bold', backgroundColor: '#5AC0B1', fontSize: 11, color: 'white' }}
                onClick={() => {this.onOpenNewCustomerContractServiceForm()}}
              >
                <ReduxLanguage languageKey="forms.createNewCustomer" />
              </Button>
            }
          </div>
          <div className={classes.tableContainer}>
            <CustomerContractServicesTable
              rowsPerPage={pageSize}
              customerContractServices={customerContractServices as PaginatedDataDTO<ICustomerContractServiceInDTO>}
              onRowClick={(customerContractService) => {this.onOpenEditCustomerContractServiceForm(customerContractService)}}
              onPageSizeChange={pageSize => this.onPageSizeChange(pageSize)}
              onFetchElementsOnPage={page => this.onFetchCustomerContractServicesOnPage(page)}
              onDeleteCustomerContractServiceRequest={(customerContractService) => this.onDeleteCustomerContractServiceRequest(customerContractService)}
              onDuplicateCustomerContractServiceRequest={(customerContractService) => this.onOpenDuplicateCustomerContractServiceForm(customerContractService)}
              hideFooter={true}
            />
          </div>
        </Card>
      </div>
    );
  }
}


function mapStateToProps(state: IState) {
  return {
    language: state.settings.language,
    userData: state.user.userData,
    selectedCustomerContract: state.credits.customerContracts.selectedCustomerContract,
    selectedCustomerContractService: state.credits.customerContractServices.selectedCustomerContractService,
    customerContractServices: state.credits.customerContractServices.data,
  };
}

const connector = connect(mapStateToProps);

export default connector(withRouter(withStyles(styles)(CustomerContractServicesSection)));